import React from 'react';
import Reveal from '../components/Reveal';
import usePageTitle from '../hooks/usePageTitle';
import { brand, images, speakerKit, booking } from '../mock';
import { useBooking } from '../context/BookingContext';
import { Download, Mic, Users, Phone, Mail, Globe, Linkedin, ArrowRight, Check } from 'lucide-react';

const SpeakerKit = () => {
  usePageTitle('Speaker Kit');
  const { openBooking } = useBooking();
  return (
    <main>
      {/* Hero */}
      <section className="relative pt-40 pb-24">
        <div className="absolute inset-0">
          <img src={images.speakerBanner} alt="Janiece A. Jean Baptiste speaking" className="w-full h-full object-cover object-[75%_center]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#241033_0%,rgba(36,16,51,0.93)_52%,rgba(67,38,94,0.45)_100%)]" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6">
          <Reveal>
            <div className="eyebrow text-silver mb-5">Speaker Kit</div>
            <h1 className="font-display text-5xl md:text-6xl text-white font-medium leading-tight max-w-3xl mb-6">
              Janiece A. Jean Baptiste, M.S.
            </h1>
            <p className="text-[#D9CEE6] font-light text-lg max-w-2xl mb-8">
              I/O Psychology Practitioner · Consultant · Coach · Educator · Nonprofit Leader. Thought leadership for people, teams, organizations, and mission-driven communities.
            </p>
            <a href={brand.speakerKitPdf} target="_blank" rel="noopener noreferrer" className="btn-ivory rounded-full px-9 py-4 text-sm tracking-luxe uppercase inline-flex items-center gap-2">
              Download Speaker Kit (PDF) <Download className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* Bio + headshot */}
      <section className="py-24 bg-[#FBFAF8]">
        <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="relative">
                <div className="absolute -inset-3 bg-silver rounded-sm opacity-60" />
                <img src={images.headshot} alt="Janiece A. Jean Baptiste" className="relative w-full object-cover rounded-sm bg-[#EDE8F2]" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <div className="eyebrow text-[#7E5C9E] mb-5">Speaker Bio</div>
              <p className="text-[#3A3444] font-light text-lg leading-relaxed mb-5">
                Janiece A. Jean Baptiste, M.S. is an Industrial-Organizational Psychology Practitioner, consultant, coach, educator, and CEO of The Answer Group Consulting, LLC. Her work sits at the intersection of people, systems, strategy, leadership, and transformation. She helps individuals, leaders, teams, and organizations move from vision to intentional action with clarity, alignment, and discernment.
              </p>
              <p className="text-[#3A3444] font-light text-lg leading-relaxed">
                Janiece brings experience across global for-profit organizations, nonprofit leadership, board service, higher education, and community-centered initiatives. She is known for translating complex psychological and organizational concepts into practical insight, thoughtful strategy, and meaningful movement.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Available for + Audience */}
      <section className="py-24 bg-[#241033]">
        <div className="max-w-[1280px] mx-auto px-6 grid md:grid-cols-2 gap-14">
          <Reveal>
            <div className="flex items-center gap-3 mb-8">
              <Mic className="w-5 h-5 text-[#D9CEE6]" strokeWidth={1.5} />
              <div className="eyebrow text-[#7E5C9E]">Available For</div>
            </div>
            <ul className="space-y-4">
              {speakerKit.available.map((a) => (
                <li key={a} className="flex items-center gap-3 text-white font-light text-lg">
                  <span className="w-6 h-6 rounded-full bg-silver flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-[#241033]" strokeWidth={2} /></span>
                  {a}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex items-center gap-3 mb-8">
              <Users className="w-5 h-5 text-[#D9CEE6]" strokeWidth={1.5} />
              <div className="eyebrow text-[#7E5C9E]">Audience Fit</div>
            </div>
            <div className="flex flex-wrap gap-3">
              {speakerKit.audience.map((a) => (
                <span key={a} className="text-sm text-[#E7E2EE] font-light border border-[#43265E] rounded-full px-4 py-2">{a}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Signature topics */}
      <section className="py-24 bg-[#FBFAF8]">
        <div className="max-w-[1280px] mx-auto px-6">
          <Reveal>
            <div className="text-center mb-16">
              <div className="eyebrow text-[#7E5C9E] mb-5">Signature Speaking & Workshop Topics</div>
              <h2 className="font-display text-4xl md:text-5xl text-[#241033] font-medium">Talks that translate insight into movement.</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {speakerKit.topics.map((t, i) => (
              <Reveal key={t.title} delay={(i % 3) * 80}>
                <div className="card-lift bg-white rounded-sm p-7 h-full border-l-2 border-[#43265E]">
                  <h3 className="font-display text-xl text-[#241033] mb-3 leading-snug">{t.title}</h3>
                  <p className="text-[#6A6570] font-light text-sm leading-relaxed">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Booking contact */}
      <section className="py-24 bg-[#43265E] relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#5B3A7A]/40 blur-3xl" />
        <div className="relative max-w-[1000px] mx-auto px-6 text-center">
          <div className="eyebrow text-[#B9A9CC] mb-6">Book Janiece</div>
          <h2 className="font-display text-4xl md:text-5xl text-white font-medium mb-8">Invite Janiece to your stage, team, or table.</h2>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 mb-10">
            <a href={`mailto:${brand.speakerEmail}`} className="inline-flex items-center gap-2 text-[#E7E2EE] hover:text-white font-light transition-colors"><Mail className="w-4 h-4" strokeWidth={1.5} /> {brand.speakerEmail}</a>
            <a href={`tel:${brand.phone.replace(/[^0-9]/g, '')}`} className="inline-flex items-center gap-2 text-[#E7E2EE] hover:text-white font-light transition-colors"><Phone className="w-4 h-4" strokeWidth={1.5} /> {brand.phone}</a>
            <a href="https://www.tagconsulting.llc" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#E7E2EE] hover:text-white font-light transition-colors"><Globe className="w-4 h-4" strokeWidth={1.5} /> tagconsulting.llc</a>
            <span className="inline-flex items-center gap-2 text-[#E7E2EE] font-light"><Linkedin className="w-4 h-4" strokeWidth={1.5} /> Janiece Jean Baptiste</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button onClick={() => openBooking(booking.alignmentLab, 'Book a Session')} className="btn-ivory rounded-full px-9 py-4 text-sm tracking-luxe uppercase inline-flex items-center gap-2">
              Book a Session <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <a href={brand.speakerKitPdf} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#7E5C9E] rounded-full px-9 py-4 text-sm tracking-luxe uppercase text-white hover:bg-[#5B3A7A] transition-all">
              Download Kit <Download className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SpeakerKit;
